import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Bot, FileText, Rss, Database, Plug } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, SectionHead } from "./Reveal";
import { FieldNotes } from "./FieldNotes";

const API = process.env.REACT_APP_BACKEND_URL;

export const fetchKnowledge = (lang) => fetch(`${API}/api/knowledge?lang=${lang}`).then((r) => r.json());

export const ArticleCard = ({ a, lang, k, testid }) => (
  <Link
    to={`/knowledge/${a.slug}${lang === "en" ? "" : `?lang=${lang}`}`}
    data-testid={testid}
    className="group flex h-full flex-col border border-technical bg-white p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(122,22,32,0.45)] hover:shadow-[0_18px_40px_-20px_rgba(22,24,27,0.35)]"
  >
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#525964]">
        {a.reading_minutes} {k.minRead}
      </span>
      <ArrowUpRight className="h-4 w-4 text-[#525964] transition-all duration-300 group-hover:text-red-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </div>
    <h3 className="mt-4 font-display text-lg sm:text-xl font-bold leading-snug tracking-tight text-ink group-hover:text-red-brand transition-colors duration-200">
      {a.title}
    </h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#525964]">{a.summary}</p>
    <div className="mt-5 flex flex-wrap gap-1.5">
      {a.tags.slice(0, 3).map((tag) => (
        <span key={tag} className="border border-technical px-2 py-0.5 font-mono-tech text-[9.5px] uppercase tracking-[0.15em] text-[#525964]">
          {tag}
        </span>
      ))}
    </div>
  </Link>
);

const AGENT_LINKS = [
  { icon: FileText, key: "llms", href: "/llms.txt" },
  { icon: FileText, key: "llmsFull", href: "/llms-full.txt" },
  { icon: BookOpen, key: "markdown", href: "/api/knowledge.md" },
  { icon: Plug, key: "mcp", href: "/api/mcp", noOpen: true },
  { icon: Database, key: "datasets", href: "/api/datasets" },
  { icon: Rss, key: "feed", href: "/api/feed.xml" },
  { icon: Bot, key: "agentCard", href: "/.well-known/agent-card.json" },
  { icon: FileText, key: "openapi", href: "/api/openapi.json" },
];

export const AgentPanel = ({ k, compact = false, lang = "en" }) => (
  <div id="datasets" data-testid="agent-panel" className={`border border-[#f8f7f4]/15 bg-ink bp-grid-dark ${compact ? "p-6" : "p-7 sm:p-10"}`}>
    <p className="font-mono-tech text-[0.65rem] tracking-[0.28em] uppercase text-[#e63946]">{k.agents.eyebrow}</p>
    <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#f8f7f4]">{k.agents.title}</h3>
    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#f8f7f4]/65">{k.agents.desc}</p>
    <ul className={`mt-6 grid gap-2 ${compact ? "grid-cols-1" : "sm:grid-cols-2"}`}>
      {AGENT_LINKS.map(({ icon: Icon, key, href: base, noOpen }) => {
        const href = key === "markdown" && lang !== "en" ? `${base}?lang=${lang}` : base;
        return (
        <li key={key}>
          <a
            href={href}
            target={noOpen ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={noOpen ? (e) => e.preventDefault() : undefined}
            data-testid={`agent-link-${key}`}
            className="flex items-start gap-3 border border-[#f8f7f4]/10 bg-[#f8f7f4]/[0.04] px-4 py-3 transition-colors duration-200 hover:border-[#e63946]/60 hover:bg-[#f8f7f4]/[0.07]"
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#e63946]" />
            <span className="min-w-0">
              <span className="block font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4]">{k.agents.links[key]}</span>
              <span className="block truncate font-mono-tech text-[10.5px] text-[#f8f7f4]/45">margtor.africa{href}</span>
            </span>
          </a>
        </li>
        );
      })}
    </ul>
    <p className="mt-6 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]/50">
      {k.license.label}: <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer license" className="text-[#f8f7f4]/80 underline decoration-[#e63946]/60 underline-offset-4">CC BY 4.0</a> — {k.license.attribution}
    </p>
  </div>
);

export const KnowledgePage = () => {
  const { lang, t } = useLang();
  const k = t.knowledge;
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [openTerm, setOpenTerm] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (data && window.location.hash === "#notes") document.getElementById("notes")?.scrollIntoView({ behavior: "smooth" });
  }, [data]);
  useEffect(() => {
    setFailed(false);
    fetchKnowledge(lang).then(setData).catch(() => setFailed(true));
  }, [lang]);

  return (
    <main data-testid="knowledge-page">
      <section className="relative bg-canvas bp-grid pt-[68px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 pt-16 sm:pt-24 pb-16">
          <div className="max-w-3xl">
            <Reveal>
              <p className="eyebrow flex items-center gap-3" data-testid="knowledge-eyebrow">
                <span className="inline-block h-px w-10 bg-red-brand" />
                {k.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 data-testid="knowledge-title" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-ink">
                {k.title}
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-[#525964]">{k.desc}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="articles" className="relative bg-sand border-y border-technical py-20 sm:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <SectionHead eyebrow={k.articlesEyebrow} title={k.articlesTitle} testid="knowledge-articles" />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="knowledge-article-grid">
            {(data?.articles || []).map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                <ArticleCard a={a} lang={lang} k={k} testid={`article-card-${a.slug}`} />
              </Reveal>
            ))}
            {!data && !failed && [0, 1, 2].map((i) => <div key={i} className="h-64 animate-pulse border border-technical bg-white/60" />)}
          </div>
          {failed && (
            <div data-testid="knowledge-error" className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 border border-dashed border-[rgba(122,22,32,0.4)] bg-white px-5 py-4">
              <p className="text-sm text-[#525964]">{k.loadError}</p>
              <button type="button" data-testid="knowledge-retry" onClick={() => { setFailed(false); fetchKnowledge(lang).then(setData).catch(() => setFailed(true)); }} className="chamfer-sm bg-ink px-4 py-2 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:bg-red-brand transition-colors">{k.retry}</button>
            </div>
          )}
        </div>
      </section>

      <FieldNotes notes={data?.notes} />

      <section id="glossary" className="relative bg-canvas bp-grid py-20 sm:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHead eyebrow={k.glossaryEyebrow} title={k.glossaryTitle} testid="knowledge-glossary" />
            <dl className="mt-10 divide-y divide-technical border-y border-technical" data-testid="glossary-list">
              {(data?.glossary || []).map((g) => (
                <div key={g.id} className="py-1">
                  <dt>
                    <button
                      type="button"
                      data-testid={`glossary-term-${g.id}`}
                      aria-expanded={openTerm === g.id}
                      onClick={() => setOpenTerm(openTerm === g.id ? null : g.id)}
                      className="flex w-full items-center justify-between gap-4 py-4 text-left font-display text-base sm:text-lg font-bold tracking-tight text-ink hover:text-red-brand transition-colors duration-200"
                    >
                      {g.term}
                      <span className={`font-mono-tech text-lg text-red-brand transition-transform duration-300 ${openTerm === g.id ? "rotate-45" : ""}`}>+</span>
                    </button>
                  </dt>
                  <dd className={`overflow-hidden transition-all duration-300 ${openTerm === g.id ? "max-h-96 pb-5 opacity-100" : "max-h-0 opacity-0"}`}>
                    <p className="max-w-2xl text-sm leading-relaxed text-[#525964]">{g.definition}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5 lg:pt-20">
            <Reveal delay={0.1}>
              <AgentPanel k={k} lang={lang} />
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};
